import { memo, useCallback, useState } from "react";
import { push } from "react-router-redux";
import { t } from "ttag";

import {
  useUpdateTableFieldsOrderMutation,
  useUpdateTableMutation,
} from "metabase/api";
import { EmptyState } from "metabase/common/components/EmptyState";
import { ForwardRefLink } from "metabase/common/components/Link";
import { trackDependencyEntitySelected } from "metabase/data-studio/analytics";
import {
  FieldOrderPicker,
  NameDescriptionInput,
} from "metabase/metadata/components";
import { ResponsiveButton } from "metabase/metadata/components/ResponsiveButton";
import { TableFieldList } from "metabase/metadata/components/TableFieldList";
import { TableSortableFieldList } from "metabase/metadata/components/TableSortableFieldList";
import { useMetadataToasts } from "metabase/metadata/hooks";
import { getRawTableFieldId } from "metabase/metadata/utils/field";
import {
  PLUGIN_DEPENDENCIES,
  PLUGIN_LIBRARY,
  PLUGIN_REMOTE_SYNC,
  PLUGIN_REPLACEMENT,
} from "metabase/plugins";
import { useDispatch, useSelector } from "metabase/redux";
import {
  Box,
  Button,
  Group,
  Icon,
  Loader,
  Stack,
  Tabs,
  Tooltip,
} from "metabase/ui";
import * as Urls from "metabase/urls";
import type { DataStudioTableMetadataTab } from "metabase/urls/data-studio";
import { dependencyGraph } from "metabase/urls/dependencies";
import {
  type FieldId,
  type Table,
  type TableFieldOrder,
  isConcreteTableId,
} from "metabase-types/api";

import S from "./TableSection.module.css";
import { MeasureList } from "./components/MeasureList";
import { SegmentList } from "./components/SegmentList";
import { TableAttributesEditSingle } from "./components/TableAttributesEditSingle";
import { TableCollection } from "./components/TableCollection";
import { TableMetadata } from "./components/TableMetadata";
import { TableSectionGroup } from "./components/TableSectionGroup";

interface Props {
  table: Table;
  activeFieldId?: FieldId;
  activeTab: DataStudioTableMetadataTab;
  canPublish: boolean;
  hasLibrary: boolean;
  onSyncOptionsClick: () => void;
  onUpdate: () => void;
}

type TableModalType = "library" | "publish" | "unpublish" | "replace";

const TableSectionBase = ({
  table,
  activeFieldId,
  activeTab,
  canPublish,
  hasLibrary,
  onSyncOptionsClick,
  onUpdate,
}: Props) => {
  const [updateTable] = useUpdateTableMutation();
  const [updateTableSorting, { isLoading: isUpdatingSorting }] =
    useUpdateTableMutation();
  const [updateTableFieldsOrder] = useUpdateTableFieldsOrderMutation();
  const [modalType, setModalType] = useState<TableModalType>();
  const { sendErrorToast, sendSuccessToast, sendUndoToast } =
    useMetadataToasts();
  const [isSorting, setIsSorting] = useState(false);
  const hasFields = Boolean(table.fields && table.fields.length > 0);
  const isLibraryEnabled = PLUGIN_LIBRARY.isEnabled;
  const isDependencyGraphEnabled = PLUGIN_DEPENDENCIES.isEnabled;
  const remoteSyncReadOnly = useSelector(
    PLUGIN_REMOTE_SYNC.getIsRemoteSyncReadOnly,
  );

  const getFieldHref = (fieldId: FieldId) => {
    return Urls.dataStudioData({
      databaseId: table.db_id,
      schemaName: table.schema,
      tableId: table.id,
      tab: "field",
      fieldId,
    });
  };

  const dispatch = useDispatch();

  const handleTabChange = useCallback(
    (tab: string | null) => {
      if (!Urls.isDataStudioTableMetadataTab(tab)) {
        return;
      }

      dispatch(
        push(
          Urls.dataStudioData({
            databaseId: table.db_id,
            schemaName: table.schema,
            tableId: table.id,
            tab,
          }),
        ),
      );
    },
    [dispatch, table.db_id, table.schema, table.id],
  );

  const handleNameChange = async (name: string) => {
    const { error } = await updateTable({
      id: table.id,
      display_name: name,
    });

    if (error) {
      sendErrorToast(t`Failed to update table name`);
    } else {
      onUpdate();
      sendSuccessToast(t`Table name updated`, async () => {
        const { error } = await updateTable({
          id: table.id,
          display_name: table.display_name,
        });
        sendUndoToast(error);
      });
    }
  };

  const handleDescriptionChange = async (description: string) => {
    const { error } = await updateTable({ id: table.id, description });

    if (error) {
      sendErrorToast(t`Failed to update table description`);
    } else {
      sendSuccessToast(t`Table description updated`, async () => {
        const { error } = await updateTable({
          id: table.id,
          description: table.description ?? "",
        });
        sendUndoToast(error);
      });
    }
  };

  const handleFieldOrderTypeChange = async (fieldOrder: TableFieldOrder) => {
    const { error } = await updateTableSorting({
      id: table.id,
      field_order: fieldOrder,
    });

    if (error) {
      sendErrorToast(t`Failed to update field order`);
    } else {
      sendSuccessToast(t`Field order updated`, async () => {
        const { error } = await updateTable({
          id: table.id,
          field_order: table.field_order,
        });
        sendUndoToast(error);
      });
    }
  };

  const handleCustomFieldOrderChange = async (fieldOrder: FieldId[]) => {
    const { error } = await updateTableFieldsOrder({
      id: table.id,
      field_order: fieldOrder,
    });

    if (error) {
      sendErrorToast(t`Failed to update field order`);
    } else {
      sendSuccessToast(t`Field order updated`, async () => {
        const { error: fieldsOrderError } = await updateTableFieldsOrder({
          id: table.id,
          field_order: table.fields?.map(getRawTableFieldId) ?? [],
        });

        if (table.field_order !== "custom") {
          const { error: tableError } = await updateTable({
            id: table.id,
            field_order: table.field_order,
          });
          sendUndoToast(fieldsOrderError ?? tableError);
        } else {
          sendUndoToast(fieldsOrderError);
        }
      });
    }
  };

  const handlePublishToggle = () => {
    if (!hasLibrary) {
      setModalType("library");
    } else {
      setModalType(table.is_published ? "unpublish" : "publish");
    }
  };

  const handleCloseModal = () => {
    setModalType(undefined);
  };

  const handleSuccessCloseModal = () => {
    onUpdate();
    handleCloseModal();
  };

  const registerDependencyGraphTrackingEvent = () => {
    if (isConcreteTableId(table.id)) {
      trackDependencyEntitySelected({
        entityId: table.id,
        triggeredFrom: "data-structure",
        eventDetail: "table",
      });
    }
  };

  return (
    <Stack data-testid="table-section" gap="md" pb="xl">
      <Box>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List mb="md">
            <Tabs.Tab
              value="details"
              leftSection={<Icon name="info" />}
            >{t`Details`}</Tabs.Tab>
            <Tabs.Tab
              value="field"
              leftSection={<Icon name="list" />}
            >{t`Fields`}</Tabs.Tab>
            <Tabs.Tab
              value="segments"
              leftSection={<Icon name="segment" />}
            >{t`Segments`}</Tabs.Tab>
            <Tabs.Tab
              value="measures"
              leftSection={<Icon name="ruler" />}
            >{t`Measures`}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details">
            <Stack gap="md">
              <Box className={S.header}>
                <NameDescriptionInput
                  description={table.description ?? ""}
                  descriptionPlaceholder={t`Give this table a description`}
                  name={table.display_name}
                  nameIcon="table2"
                  nameMaxLength={254}
                  namePlaceholder={t`Give this table a name`}
                  onNameChange={handleNameChange}
                  onDescriptionChange={handleDescriptionChange}
                />
              </Box>

              <Group justify="stretch" gap="sm">
                {canPublish && isLibraryEnabled && !remoteSyncReadOnly && (
                  <Button
                    flex="1"
                    p="sm"
                    leftSection={
                      <Icon
                        name={table.is_published ? "unpublish" : "publish"}
                      />
                    }
                    onClick={handlePublishToggle}
                  >
                    {table.is_published ? t`Unpublish` : t`Publish`}
                  </Button>
                )}
                {!table.db?.is_attached_dwh && (
                  <Button
                    flex="1"
                    leftSection={<Icon name="settings" />}
                    onClick={onSyncOptionsClick}
                  >
                    {t`Sync settings`}
                  </Button>
                )}
                <PLUGIN_REPLACEMENT.SourceReplacementButton>
                  {({ tooltip, isDisabled }) => (
                    <Tooltip label={tooltip ?? t`Find and replace`}>
                      <Button
                        p="sm"
                        w="2.5rem"
                        flex="0 1 auto"
                        leftSection={<Icon name="find_replace" />}
                        aria-label={t`Find and replace`}
                        disabled={isDisabled}
                        onClick={() => setModalType("replace")}
                      />
                    </Tooltip>
                  )}
                </PLUGIN_REPLACEMENT.SourceReplacementButton>
                {isDependencyGraphEnabled && (
                  <Tooltip label={t`Dependency graph`}>
                    <Button
                      component={ForwardRefLink}
                      to={dependencyGraph({
                        entry: { id: Number(table.id), type: "table" },
                      })}
                      p="sm"
                      w="2.5rem"
                      flex="0 1 auto"
                      leftSection={<Icon name="dependencies" />}
                      aria-label={t`Dependency graph`}
                      onClickCapture={registerDependencyGraphTrackingEvent}
                      onAuxClick={registerDependencyGraphTrackingEvent}
                    />
                  </Tooltip>
                )}

                <Tooltip label={t`Schema viewer`}>
                  <Button
                    component={ForwardRefLink}
                    to={Urls.dataStudioSchemaViewer({
                      databaseId: table.db_id,
                      schema: table.schema,
                      tableIds: [table.id],
                    })}
                    p="sm"
                    leftSection={<Icon name="network" />}
                    style={{
                      flexGrow: 0,
                      width: 40,
                    }}
                    aria-label={t`Schema viewer`}
                  />
                </Tooltip>

                <Box style={{ flexGrow: 0, width: 40 }}>
                  <TableLink table={table} />
                </Box>
              </Group>

              <TableAttributesEditSingle table={table} onUpdate={onUpdate} />

              <TableSectionGroup title={t`Metadata`}>
                <TableMetadata table={table} />
              </TableSectionGroup>

              {table.is_published && <TableCollection table={table} />}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="field">
            <Stack gap="md">
              <Group gap="md" justify="flex-start" wrap="nowrap">
                {isUpdatingSorting && (
                  <Loader data-testid="loading-indicator" size="xs" />
                )}

                {!isSorting && hasFields && (
                  <ResponsiveButton
                    icon="sort_arrows"
                    showLabel
                    onClick={() => setIsSorting(true)}
                  >{t`Sorting`}</ResponsiveButton>
                )}

                {isSorting && (
                  <FieldOrderPicker
                    value={table.field_order}
                    onChange={handleFieldOrderTypeChange}
                  />
                )}

                {isSorting && (
                  <ResponsiveButton
                    icon="check"
                    showLabel
                    onClick={() => setIsSorting(false)}
                  >{t`Done`}</ResponsiveButton>
                )}
              </Group>

              {!hasFields && (
                <EmptyState
                  className={S.EmptyState}
                  message={t`This table has no fields`}
                  spacing="sm"
                />
              )}

              {hasFields && (
                <>
                  <Box
                    style={{
                      display: isSorting ? "block" : "none",
                    }}
                    aria-hidden={!isSorting}
                  >
                    <TableSortableFieldList
                      activeFieldId={activeFieldId}
                      table={table}
                      onChange={handleCustomFieldOrderChange}
                    />
                  </Box>

                  <Box
                    style={{
                      display: isSorting ? "none" : "block",
                    }}
                    aria-hidden={isSorting}
                  >
                    <TableFieldList
                      table={table}
                      activeFieldId={activeFieldId}
                      getFieldHref={getFieldHref}
                    />
                  </Box>
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="segments">
            <SegmentList table={table} />
          </Tabs.Panel>

          <Tabs.Panel value="measures">
            <MeasureList table={table} />
          </Tabs.Panel>
        </Tabs>
      </Box>

      <PLUGIN_LIBRARY.CreateLibraryModal
        title={t`First, let's create your Library`}
        explanatorySentence={t`This is where published tables will go.`}
        isOpened={modalType === "library"}
        onCreate={() => setModalType("publish")}
        onClose={handleCloseModal}
      />
      <PLUGIN_LIBRARY.PublishTablesModal
        isOpened={modalType === "publish"}
        tableIds={[table.id]}
        onPublish={handleSuccessCloseModal}
        onClose={handleCloseModal}
      />
      <PLUGIN_LIBRARY.UnpublishTablesModal
        isOpened={modalType === "unpublish"}
        tableIds={[table.id]}
        onUnpublish={handleSuccessCloseModal}
        onClose={handleCloseModal}
      />
      <PLUGIN_REPLACEMENT.SourceReplacementModal
        opened={modalType === "replace"}
        initialSource={{ id: Number(table.id), type: "table" }}
        triggeredFrom="table_list"
        onClose={handleCloseModal}
      />
    </Stack>
  );
};

function TableLink({ table }: { table: Table }) {
  const url =
    Urls.modelToUrl({
      id: Number(table.id),
      name: table.name,
      model: "table",
      database: { id: table.db_id },
    }) ?? "#";

  return (
    <Tooltip label={t`Go to this table`} position="top">
      <Button
        component={ForwardRefLink}
        to={url}
        aria-label={t`Go to this table`}
        leftSection={<Icon name="external" size={16} />}
        style={{
          width: "100%",
        }}
      />
    </Tooltip>
  );
}

export const TableSection = memo(TableSectionBase);
